import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, Calendar as CalIcon } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none";

const STATUS_OPTIONS = ["Draft","Ready for Review","Shared With Steward","Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Withdrawn","Closed","Archived"];

function StatusPill({s}) {
  const map = {"Draft":"bg-slate-100 text-slate-700","Ready for Review":"bg-amber-100 text-amber-800","Shared With Steward":"bg-blue-100 text-blue-800","Filed":"bg-red-100 text-red-800","Meeting Scheduled":"bg-purple-100 text-purple-800","Employer Response Received":"bg-indigo-100 text-indigo-800","Pending":"bg-yellow-100 text-yellow-800","Settled":"bg-green-100 text-green-800","Denied":"bg-red-100 text-red-800","Withdrawn":"bg-slate-100 text-slate-700","Closed":"bg-slate-200 text-slate-700","Archived":"bg-slate-200 text-slate-600"};
  return <span className={`text-xs px-2 py-1 rounded font-semibold ${map[s]||"bg-slate-100 text-slate-700"}`}>{s}</span>;
}

export function StewardDashboard() {
  const [cases, setCases] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [sortBy, setSortBy] = React.useState("updated");

  React.useEffect(()=>{ api.get("/steward/cases").then(r=>setCases(r.data)); }, []);

  const filtered = React.useMemo(()=>{
    let list = [...cases];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(c =>
        (c.title||"").toLowerCase().includes(s) ||
        (c.case_number||"").toLowerCase().includes(s) ||
        (c.primary_category||"").toLowerCase().includes(s) ||
        (c.supervisor||"").toLowerCase().includes(s) ||
        (c.work_location||"").toLowerCase().includes(s)
      );
    }
    if (statusFilter) list = list.filter(c => c.status === statusFilter);
    if (sortBy === "updated") list.sort((a,b)=>(b.updated_at||"").localeCompare(a.updated_at||""));
    if (sortBy === "newest") list.sort((a,b)=>(b.created_at||"").localeCompare(a.created_at||""));
    if (sortBy === "incident") list.sort((a,b)=>(b.incident_date||"").localeCompare(a.incident_date||""));
    if (sortBy === "status") list.sort((a,b)=>(a.status||"").localeCompare(b.status||""));
    return list;
  }, [cases, q, statusFilter, sortBy]);

  const reviewQueue = cases.filter(c => c.status === "Ready for Review" || c.status === "Shared With Steward");
  const meetings = cases.filter(c => c.status === "Meeting Scheduled" || (c.steward_info?.meeting_date));
  const recent = [...cases].sort((a,b)=>(b.updated_at||"").localeCompare(a.updated_at||"")).slice(0,5);

  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display" data-testid="steward-title">Steward Dashboard</h1>
      <p className="text-slate-600 text-sm mt-1">Cases shared with you by employees.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5"><div className="text-xs text-slate-500">Total Shared</div><div className="text-3xl font-black font-display" data-testid="stat-total">{cases.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-5"><div className="text-xs text-slate-500">Review Queue</div><div className="text-3xl font-black font-display" data-testid="stat-review">{reviewQueue.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-5"><div className="text-xs text-slate-500">Meetings</div><div className="text-3xl font-black font-display" data-testid="stat-meetings">{meetings.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-md p-5"><div className="text-xs text-slate-500">Settled</div><div className="text-3xl font-black font-display">{cases.filter(c=>c.status==="Settled").length}</div></div>
      </div>

      {reviewQueue.length > 0 && (
        <section className="mt-8">
          <h2 className="text-xl font-black font-display text-slate-900 mb-4">Awaiting Your Review</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviewQueue.map(c => <CaseCard key={c.id} c={c}/>)}
          </div>
        </section>
      )}

      <section className="mt-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-xl font-black font-display text-slate-900 mr-auto">All Shared Cases</h2>
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="steward-search" placeholder="Search..." value={q} onChange={e=>setQ(e.target.value)} className={`${inputCls} pl-9`}/>
          </div>
          <select data-testid="steward-status-filter" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
            <option value="">All statuses</option>{STATUS_OPTIONS.map(s=><option key={s}>{s}</option>)}
          </select>
          <select data-testid="steward-sort" value={sortBy} onChange={e=>setSortBy(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
            <option value="updated">Recently updated</option><option value="newest">Newest</option><option value="incident">Incident date</option><option value="status">Status</option>
          </select>
        </div>
        {filtered.length===0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-slate-500 rounded-md">No shared cases match.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(c=><CaseCard key={c.id} c={c}/>)}
          </div>
        )}
      </section>
    </AppShell>
  );
}

function CaseCard({c}) {
  return (
    <Link to={`/cases/${c.id}`} className="bg-white border border-slate-200 rounded-md p-5 block transition-lift hover:shadow-md" data-testid={`steward-case-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 font-mono">{c.case_number}</div>
          <h3 className="font-bold text-slate-900 font-display truncate">{c.title}</h3>
          <div className="text-xs text-slate-500 mt-1">{c.primary_category}</div>
        </div>
        <StatusPill s={c.status}/>
      </div>
      <div className="mt-3 text-xs text-slate-500">Incident: {c.incident_date || "—"} • Updated: {c.updated_at?.slice(0,10)}</div>
      {c.steward_info?.meeting_date && (
        <div className="mt-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-2 py-1 inline-flex items-center gap-1">
          <CalIcon className="h-3 w-3"/>Meeting {c.steward_info.meeting_date}
        </div>
      )}
    </Link>
  );
}

export function StewardMeetings() {
  const [cases, setCases] = React.useState([]);
  React.useEffect(()=>{ api.get("/steward/cases").then(r=>setCases(r.data)); }, []);
  const withMeetings = cases
    .filter(c => c.steward_info?.meeting_date)
    .sort((a,b)=>(a.steward_info?.meeting_date||"").localeCompare(b.steward_info?.meeting_date||""));
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Meetings</h1>
      <p className="text-slate-600 text-sm mt-1">Upcoming grievance meetings for cases shared with you.</p>
      <div className="mt-6 space-y-3">
        {withMeetings.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500">No meetings scheduled yet. Set a meeting date on any shared case detail page.</div>
        ) : withMeetings.map(c => (
          <Link to={`/cases/${c.id}`} key={c.id} className="bg-white border border-slate-200 rounded-md p-5 flex justify-between items-center transition-lift hover:shadow-md">
            <div>
              <div className="text-xs text-slate-500 font-mono">{c.case_number}</div>
              <div className="font-bold text-slate-900 font-display">{c.title}</div>
              <div className="text-xs text-slate-500 mt-1">{c.primary_category}</div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2 text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-1 font-semibold text-sm">
                <CalIcon className="h-4 w-4"/>{c.steward_info.meeting_date}
              </div>
              {c.steward_info.meeting_location && <div className="text-xs text-slate-500 mt-1">{c.steward_info.meeting_location}</div>}
            </div>
          </Link>
        ))}
      </div>
    </AppShell>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = React.useState({});
  React.useEffect(()=>{ api.get("/admin/stats").then(r=>setStats(r.data)); }, []);
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display" data-testid="admin-title">Admin Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {[["Users",stats.users],["Cases",stats.cases],["Premium",stats.premium_users],["Articles",stats.articles],["Categories",stats.categories],["Messages",stats.contact_messages]].map(([l,v])=>(
          <div key={l} className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs text-slate-500">{l}</div>
            <div className="text-3xl font-black font-display" data-testid={`stat-${l.toLowerCase()}`}>{v ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-3 flex-wrap">
        <Link to="/admin/users"><Button variant="outline" data-testid="admin-users-link">Manage users</Button></Link>
        <Link to="/admin/cases"><Button variant="outline">Cases</Button></Link>
        <Link to="/admin/articles"><Button variant="outline">Contract articles</Button></Link>
        <Link to="/admin/categories"><Button variant="outline">Categories</Button></Link>
        <Link to="/admin/messages"><Button variant="outline">Contact messages</Button></Link>
        <Link to="/admin/contracts"><Button variant="outline" data-testid="admin-contracts-link">Contract Library (KB)</Button></Link>
        <Link to="/admin/contracts/taxonomy"><Button variant="outline">Topics & keywords</Button></Link>
      </div>
    </AppShell>
  );
}

export function AdminUsers() {
  const [users, setUsers] = React.useState([]);
  const load = async () => setUsers((await api.get("/admin/users")).data);
  React.useEffect(()=>{ load(); }, []);
  const setRole = async (id, role) => { await api.post(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); };
  const toggle = async (id, active) => { await api.post(`/admin/users/${id}/active`, { active }); toast.success(active?"Activated":"Deactivated"); load(); };
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Users</h1>
      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Email","Name","Role","Plan","Active",""].map(h=><th key={h} className="text-left p-3 font-semibold text-slate-700">{h}</th>)}</tr></thead>
          <tbody>
            {users.map(u=>(
              <tr key={u.id} className="border-t border-slate-200" data-testid={`user-row-${u.id}`}>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.first_name} {u.last_name}</td>
                <td className="p-3">
                  <select value={u.role} onChange={e=>setRole(u.id, e.target.value)} className="border border-slate-300 rounded px-2 py-1" data-testid={`role-${u.id}`}>
                    <option value="employee">employee</option><option value="steward">steward</option><option value="admin">admin</option>
                  </select>
                </td>
                <td className="p-3">{u.plan}</td>
                <td className="p-3">{u.is_active ? "Active":"Inactive"}</td>
                <td className="p-3"><Button variant="outline" size="sm" onClick={()=>toggle(u.id, !u.is_active)}>{u.is_active?"Deactivate":"Activate"}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

export function AdminCases() {
  const [cases, setCases] = React.useState([]);
  React.useEffect(()=>{ api.get("/admin/cases").then(r=>setCases(r.data)); }, []);
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">All Cases</h1>
      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Case #","Title","Status","Category","Updated"].map(h=><th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>{cases.map(c=><tr key={c.id} className="border-t border-slate-200"><td className="p-3 font-mono">{c.case_number}</td><td className="p-3">{c.title}</td><td className="p-3">{c.status}</td><td className="p-3">{c.primary_category}</td><td className="p-3">{c.updated_at?.slice(0,10)}</td></tr>)}</tbody>
        </table>
      </div>
    </AppShell>
  );
}

export function AdminMessages() {
  const [msgs, setMsgs] = React.useState([]);
  React.useEffect(()=>{ api.get("/admin/messages").then(r=>setMsgs(r.data)); }, []);
  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Contact Messages</h1>
      <div className="mt-6 space-y-3">
        {msgs.map(m=>(
          <div key={m.id} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-sm text-slate-500">{m.created_at?.slice(0,10)}</div>
            <div className="font-semibold">{m.name} <span className="text-slate-500 font-normal">&lt;{m.email}&gt;</span></div>
            <div className="font-medium mt-1">{m.subject}</div>
            <p className="text-sm mt-2 text-slate-700">{m.message}</p>
          </div>
        ))}
        {msgs.length===0 && <div className="text-slate-500">No messages.</div>}
      </div>
    </AppShell>
  );
}

const emptyArticle = { contract_name:"UPS Teamsters National Master Agreement", article_number:"", article_title:"", article_summary:"", full_text:"", category_tags:[], supplement:"", effective_dates:"", active:true, legal_review_needed:false };

export function AdminArticles() {
  const [articles, setArticles] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyArticle);
  const [showForm, setShowForm] = React.useState(false);
  const [q, setQ] = React.useState("");

  const load = async () => setArticles((await api.get("/articles")).data);
  React.useEffect(()=>{ load(); }, []);

  const openNew = () => { setForm(emptyArticle); setEditing(null); setShowForm(true); };
  const openEdit = (a) => { setForm({...emptyArticle, ...a, category_tags:a.category_tags||[]}); setEditing(a.id); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {...form, category_tags: typeof form.category_tags === "string" ? form.category_tags.split(",").map(s=>s.trim()).filter(Boolean) : form.category_tags};
    try {
      if (editing) { await api.put(`/admin/articles/${editing}`, payload); toast.success("Article updated"); }
      else { await api.post("/admin/articles", payload); toast.success("Article added"); }
      setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = q ? articles.filter(a => `${a.article_number} ${a.article_title} ${a.article_summary}`.toLowerCase().includes(q.toLowerCase())) : articles;

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-display">Contract Articles</h1>
        <Button onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-new-article">+ New Article</Button>
      </div>
      <input data-testid="admin-articles-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search articles..." className={inputCls + " mt-4 max-w-md"}/>
      <div className="mt-6 space-y-3">
        {filtered.map(a=>(
          <div key={a.id} className="bg-white border border-slate-200 rounded-md p-4 flex justify-between items-start gap-3" data-testid={`admin-article-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-red-600 font-semibold">{a.article_number} {a.supplement && <span className="ml-2 text-xs text-slate-500">• {a.supplement}</span>} {!a.active && <span className="ml-2 text-xs text-amber-600">(inactive)</span>}</div>
              <div className="font-bold text-slate-900">{a.article_title}</div>
              <div className="text-sm text-slate-600 mt-1">{a.article_summary}</div>
              {a.category_tags?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{a.category_tags.map(t=><span key={t} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-600">{t}</span>)}</div>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={()=>openEdit(a)} data-testid={`edit-article-${a.id}`}>Edit</Button>
              <Button size="sm" variant="outline" onClick={()=>del(a.id)} data-testid={`del-article-${a.id}`}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={()=>setShowForm(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-md max-w-2xl w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="article-form">
            <h2 className="text-xl font-black font-display">{editing ? "Edit Article" : "New Article"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block"><span className="text-sm font-medium text-slate-700">Contract name</span><input className={inputCls} value={form.contract_name} onChange={e=>setForm({...form,contract_name:e.target.value})} required/></label>
              <label className="block"><span className="text-sm font-medium text-slate-700">Article number</span><input data-testid="af-number" className={inputCls} value={form.article_number} onChange={e=>setForm({...form,article_number:e.target.value})} required/></label>
              <label className="block md:col-span-2"><span className="text-sm font-medium text-slate-700">Article title</span><input data-testid="af-title" className={inputCls} value={form.article_title} onChange={e=>setForm({...form,article_title:e.target.value})} required/></label>
              <label className="block md:col-span-2"><span className="text-sm font-medium text-slate-700">Summary (plain-language)</span><textarea data-testid="af-summary" rows={3} className={inputCls} value={form.article_summary} onChange={e=>setForm({...form,article_summary:e.target.value})} required/></label>
              <label className="block md:col-span-2"><span className="text-sm font-medium text-slate-700">Category tags (comma-separated)</span><input className={inputCls} value={Array.isArray(form.category_tags)?form.category_tags.join(", "):form.category_tags} onChange={e=>setForm({...form,category_tags:e.target.value})}/></label>
              <label className="block"><span className="text-sm font-medium text-slate-700">Supplement / rider</span><input className={inputCls} value={form.supplement||""} onChange={e=>setForm({...form,supplement:e.target.value})}/></label>
              <label className="block"><span className="text-sm font-medium text-slate-700">Effective dates</span><input className={inputCls} value={form.effective_dates||""} onChange={e=>setForm({...form,effective_dates:e.target.value})}/></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e=>setForm({...form,active:e.target.checked})}/> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.legal_review_needed} onChange={e=>setForm({...form,legal_review_needed:e.target.checked})}/> Legal review needed</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={()=>setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" data-testid="af-save">Save</Button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}

export function AdminCategories() {
  const [cats, setCats] = React.useState([]);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [editing, setEditing] = React.useState(null);

  const load = async () => setCats((await api.get("/admin/categories")).data);
  React.useEffect(()=>{ load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      if (editing) { await api.put(`/admin/categories/${editing}`, { name, description }); toast.success("Updated"); }
      else { await api.post("/admin/categories", { name, description }); toast.success("Added"); }
      setName(""); setDescription(""); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); load();
  };

  return (
    <AppShell>
      <h1 className="text-3xl font-black font-display">Grievance Categories</h1>
      <form onSubmit={save} className="mt-6 bg-white border border-slate-200 rounded-md p-5 space-y-3 max-w-2xl">
        <div className="text-sm font-semibold text-slate-700">{editing ? "Edit category" : "Add new category"}</div>
        <input data-testid="cat-name" placeholder="Name" className={inputCls} value={name} onChange={e=>setName(e.target.value)}/>
        <input data-testid="cat-desc" placeholder="Description (optional)" className={inputCls} value={description} onChange={e=>setDescription(e.target.value)}/>
        <div className="flex gap-2">
          <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" data-testid="cat-save">{editing ? "Save" : "Add"}</Button>
          {editing && <Button type="button" variant="outline" onClick={()=>{setEditing(null);setName("");setDescription("");}}>Cancel</Button>}
        </div>
      </form>
      <div className="mt-6 bg-white border border-slate-200 rounded-md divide-y divide-slate-200">
        {cats.map(c=>(
          <div key={c.id} className="p-4 flex justify-between items-center" data-testid={`cat-row-${c.id}`}>
            <div>
              <div className="font-semibold text-slate-900">{c.name}</div>
              {c.description && <div className="text-xs text-slate-500">{c.description}</div>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={()=>{setEditing(c.id);setName(c.name);setDescription(c.description||"");}}>Edit</Button>
              <Button size="sm" variant="outline" onClick={()=>del(c.id)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
